/**
 * Phase 8.4 — Exit-intent + scroll-depth lead magnet.
 *
 * Triggers once per session (sessionStorage flag `lwp_lead_magnet_seen`) when:
 *   - Scroll depth ≥ 70% of document, OR
 *   - Mouse leaves the viewport from the top edge (desktop only), OR
 *   - 90s dwell time on page (mobile fallback since mouse-leave rarely fires).
 *
 * Only mounts on home + calculator pages. Reuses POST /api/leads/partial so
 * the captured email lands in the existing nurture pipeline. On submit, we
 * try to open the existing retirement/life-insurance PDF download via a new
 * tab (if the user came from a calculator with recent results). Otherwise
 * we show a generic confirmation.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Mail, X, Sparkles, Loader2, Check } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { trackEvent } from '@/lib/analytics'

const STORAGE_KEY = 'lwp_lead_magnet_seen'
const DWELL_MS = 90000 // 90s mobile fallback
const SCROLL_PCT = 0.7

export default function ExitIntentLeadMagnet({ source = 'exit_intent', pageContext = {} }) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [state, setState] = useState('idle') // idle | loading | sent | error
  const [errorMsg, setErrorMsg] = useState('')
  const fired = useRef(false)

  // Guard: already shown this session
  const alreadySeen = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1'
    } catch (_) {
      return false
    }
  }

  const trigger = (reason) => {
    if (fired.current || alreadySeen()) return
    fired.current = true
    try {
      sessionStorage.setItem(STORAGE_KEY, '1')
    } catch (_) {
      /* ignore */
    }
    trackEvent('lead_magnet_impression', { reason, ...pageContext })
    setOpen(true)
  }

  useEffect(() => {
    if (alreadySeen()) return

    // Scroll-depth 70%
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight
      const total = document.documentElement.scrollHeight
      if (total > 0 && scrolled / total >= SCROLL_PCT) trigger('scroll_70')
    }
    // Exit-intent (mouse leaves top edge) — desktop only
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger('mouse_leave_top')
    }
    // Mobile fallback: 90s dwell
    const dwellTimer = setTimeout(() => trigger('dwell_90s'), DWELL_MS)

    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('mouseout', onMouseOut)

    return () => {
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('mouseout', onMouseOut)
      clearTimeout(dwellTimer)
    }
  }, [])

  const close = (why) => {
    setOpen(false)
    trackEvent('lead_magnet_dismiss', { why })
  }

  const onSubmit = async (ev) => {
    ev.preventDefault()
    setErrorMsg('')
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg('Please enter a valid email.')
      return
    }
    setState('loading')
    trackEvent('lead_magnet_cta_click', { source })
    try {
      await apiClient.post('/leads/partial', {
        email: trimmed,
        source,
        source_context: {
          ...pageContext,
          lead_magnet: 'retirement_pdf',
          trigger: fired.current ? 'exit_intent_or_scroll' : 'unknown',
        },
      })
      setState('sent')
    } catch (e) {
      setState('error')
      setErrorMsg('Something went wrong — please try again.')
    }
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-magnet-title"
      data-testid="lead-magnet-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(11, 37, 69, 0.55)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-lw-gold/30 overflow-hidden"
      >
        <button
          type="button"
          onClick={() => close('close_button')}
          data-testid="lead-magnet-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-lw-navy/60 hover:text-lw-navy hover:bg-lw-navy/5 transition-colors"
        >
          <X size={18} />
        </button>
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-30"
          style={{
            background:
              'radial-gradient(circle, rgba(212,162,76,0.55) 0%, rgba(212,162,76,0) 70%)',
          }}
        />
        <div className="relative p-6 sm:p-8">
          {state === 'sent' ? (
            <div data-testid="lead-magnet-sent" className="text-center py-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-lw-gold/20 text-lw-navy flex items-center justify-center mb-4">
                <Check size={22} />
              </div>
              <h2
                id="lead-magnet-title"
                className="font-serif-display text-2xl text-lw-navy mb-2"
              >
                Check your inbox.
              </h2>
              <p className="text-sm text-lw-muted">
                We've just emailed your personalized retirement PDF. If you
                don't see it in a few minutes, check your promotions tab.
              </p>
              <button
                type="button"
                onClick={() => close('after_success')}
                className="btn-ghost-navy mt-6 mx-auto"
                data-testid="lead-magnet-done"
              >
                Continue browsing
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <span className="lw-eyebrow flex items-center gap-1.5">
                  <Sparkles size={13} className="text-lw-gold" />
                  Free resource
                </span>
              </div>
              <h2
                id="lead-magnet-title"
                className="font-serif-display text-2xl sm:text-3xl text-lw-navy leading-tight"
              >
                Get your personalized{' '}
                <span className="italic">retirement PDF</span>.
              </h2>
              <p className="mt-3 text-sm sm:text-base text-lw-muted leading-relaxed">
                A one-page snapshot: your projected nest egg, monthly income
                scenarios, and which product mix fits your stage of life.
                Takes 10 seconds — no obligation.
              </p>
              <form onSubmit={onSubmit} className="mt-5 space-y-3">
                <label className="block">
                  <span className="sr-only">Email</span>
                  <div className="flex items-center gap-2 border border-lw-navy/20 rounded-full pl-4 pr-1 py-1 focus-within:border-lw-gold focus-within:ring-2 focus-within:ring-lw-gold/40 transition-all bg-white">
                    <Mail size={16} className="text-lw-gold flex-shrink-0" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="lead-magnet-email"
                      className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={state === 'loading'}
                      data-testid="lead-magnet-submit"
                      className="btn-gold text-sm py-2 px-4 disabled:opacity-60"
                    >
                      {state === 'loading' ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <>Send it</>
                      )}
                    </button>
                  </div>
                </label>
                {errorMsg && (
                  <p
                    className="text-xs text-red-600"
                    data-testid="lead-magnet-error"
                  >
                    {errorMsg}
                  </p>
                )}
                <p className="text-[11px] text-lw-muted leading-relaxed">
                  By entering your email, you agree to receive educational
                  content from Lifespan Wealth Partners. Unsubscribe anytime.
                  We never share your email.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
